import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Add as AddIcon,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';

interface NavigationBarProps {
  onSignOut: () => void;
  onMenuToggle?: () => void;
  onNewChat?: () => void;
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  onSignOut,
  onMenuToggle,
  onNewChat,
}) => {
  const navigate = useNavigate();
  const { insuranceReportId } = useWebSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleBackToSummary = () => {
    if (insuranceReportId) {
      navigate(`/insurance-summaries/${insuranceReportId}`);
    }
  };

  return (
    <AppBar 
      position="fixed" 
      color="primary"
      elevation={2}
      sx={{ 
        zIndex: (theme) => theme.zIndex.appBar + 1,
        borderRadius: 0, // Remove rounded corners
        width: '100%',
        top: 0,
        left: 0,
      }}
    >
      <Toolbar>
        {/* Mobile Menu Toggle */}
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={onMenuToggle}
            sx={{
              mr: 2,
              color: 'rgba(0, 0, 0, 0.87)',
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Back to Summary Button */}
        {insuranceReportId && (
          <IconButton
            color="inherit"
            onClick={handleBackToSummary}
            sx={{
              mr: 2,
              color: 'rgba(0, 0, 0, 0.87)',
            }}
            title="Back to Insurance Summary"
            aria-label="Back to Insurance Summary"
          >
            <ArrowBack />
            {!isMobile && (
              <Typography sx={{ ml: 0.5, fontSize: '0.875rem' }}>
                Back to Summary
              </Typography>
            )}
          </IconButton>
        )}

        {/* Brand Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={new URL('../assets/gekkovet-logo.png', import.meta.url).href}
            alt="Gekkovet Logo"
            sx={{ 
              mr: 1.5, 
              height: 40,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              color: 'rgba(0, 0, 0, 0.87)',
              fontWeight: 300, // Much thinner font
              fontSize: '1.1rem', // Smaller size
              letterSpacing: '0.5px', // Slight letter spacing for elegance
            }}
          >
          </Typography>
          
          {/* Early Access Badge - Hidden on mobile */}
          <Chip
            label={isMobile ? 'EA' : 'Early Access'}
            size="small"
            sx={{
              ml: 2,
              display: { xs: 'none', md: 'flex' }, // Hide on mobile, show on desktop
              backgroundColor: 'rgba(255, 255, 255, 0.9)', // White background for contrast
              color: '#333333', // Dark text for readability
              border: `1px solid rgba(255, 255, 255, 0.2)`, // Subtle white border
              fontSize: '0.7rem',
              fontWeight: 600, // Slightly bolder for better visibility
              height: 24,
              textTransform: 'uppercase' as const,
              letterSpacing: '0.5px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)', // Subtle shadow for depth
              '&:hover': {
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.25)',
              },
              // Ensure accessibility
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
            aria-label="Early Access Preview"
            role="status"
          />
        </Box>
        
        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Desktop Logout Button */}
        {!isMobile && (
          <Button
            variant="outlined"
            onClick={onSignOut}
            sx={{
              mr: 2,
              borderColor: 'rgba(0, 0, 0, 0.23)',
              color: 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Sign Out
          </Button>
        )}

        {/* Mobile New Chat Button */}
        {isMobile && onNewChat && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onNewChat}
            sx={{
              mr: 1,
              borderColor: 'rgba(0, 0, 0, 0.23)',
              color: 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            New
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default NavigationBar;
